/**
 * ai-train-page.tsx — Màn TRAIN AI độc lập cho một bot (phong cách SMAX GenAI):
 *   TRÁI  = Editor tài liệu đang chọn (prompt/kịch bản — kịch bản có đủ trường động)
 *   GIỮA  = Cấu trúc bộ não (tài liệu bot + tài liệu nền + kịch bản + NGUỒN TRI THỨC)
 *   PHẢI  = Chat thử (sandbox thật — chạy đúng pipeline router → tool → generator)
 *
 * Nguồn tri thức = module động: chọn danh mục Sản phẩm/Kiến thức từ DB cấp cho bot
 * (guardrail của công cụ, enforce trong code), và thêm tài nguyên mới (Text/Product/
 * Google Sheets/CSV — chạy trên API import sẵn có của BE).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { platformLabel } from '@/pages/conversations/lib'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ArrowLeft, Save, SendHorizonal, MessageSquareText, BookOpenText, Radio, ChevronDown,
  Plus, Trash2, ClipboardCheck, Wand2, Loader2, Sparkles, Undo2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch, Separator } from '@/components/ui/misc'
import { Loading, ErrorState } from '@/components/shared/feedback'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { ResourcePickerDialog } from '@/components/ai/resource-picker-dialog'
import { PersonaFormFields, PERSONA_FORM_TRONG, chotPersonaForm } from '@/components/ai/persona-form-fields'
import { useDocFolders } from '@/hooks/use-doc-library'
import { useStartEvalRun, useEvalRuns } from '@/hooks/use-ai-eval'
import { RunDetailDialog } from './ai-eval-tab'
import { AiVietPromptDialog } from '@/components/ai/ai-viet-prompt-dialog'
import type { LoaiPrompt } from '@/hooks/use-ai'
import { api, apiError } from '@/lib/api-client'
import { useAuthStore } from '@/stores/auth-store'
import { useKnowledgeCategories } from '@/hooks/use-knowledge'
import { useZaloAccounts } from '@/hooks/use-integrations'
import { CAO_TOI_DA_10_DONG, OTimKenh, TheLocKenh, useBoLocKenh } from '@/components/shared/channel-picker'
import { CreateKnowledgeDialog } from './ai-knowledge-tab'
import { useContextBudgets, budgetForDocType, budgetCounterText } from '@/hooks/use-ai-budgets'
import { useLogicProposals } from '@/hooks/use-ai-improve'
import { KhungHuongDanDialog } from './khung-huong-dan-dialog'
import { useKhoiHuongDan } from '@/hooks/use-ai'
import { LearnHistoryDialog } from './learn-history-dialog'
import {
  aiKeys, useAiConfig, useAiScenarios, useBotTemplates,
  updateAiBot, applyBotTemplate, previewPersona, fetchScenario, createScenario, updateScenario, deleteScenario,
  createSandboxConversation, simulateBotReply,
  NHAN_VAI_TRO, CONG_CU_HANH_DONG,
  type AiBot, type AiBotInput, type BotToolsJson, type CongCuHanhDong, type PersonaForm, type VaiTroBot,
} from '@/hooks/use-ai'

const TIEN_TO_KICH_BAN_MAU = 'tpl_'

// Bảng cũ chỉ có 3 kênh nên kênh Pancake/Facebook hiện ra "#30" — dùng lại bảng
// nhãn đầy đủ của màn Hội thoại để mọi nền tảng đều có tên đọc được.
const platformName = (p?: number | null) => platformLabel(p)
const DEFAULT_EMOJI = '🤖'

// Tài liệu nền của org (AiLogicDoc) — nhãn theo màn train của hệ thống gốc
const ORG_DOCS: Array<{ type: string; label: string }> = [
  { type: 'criteria', label: 'Quy tắc trả lời (guardrails)' },
  { type: 'handoff_rules', label: 'Khi nào chuyển nhân viên' },
  { type: 'index', label: 'Vai trò & nhiệm vụ (tổng quan)' },
  { type: 'mechanism', label: 'Cách AI vận hành' },
  { type: 'persona', label: 'Tính cách & xưng hô' },
  { type: 'playbook', label: 'Kịch bản bán hàng nền' },
]

type BotDocField = 'personaPrompt' | 'playbookPrompt' | 'handoffPrompt'
type DocSel =
  | { kind: 'bot'; field: BotDocField; label: string }
  | { kind: 'org'; type: string; label: string }
  | { kind: 'scenario'; id: string; label: string }
  | { kind: 'scenario-new'; label: string }

type ScenarioForm = { name: string; description: string; loadMode: string; enabled: boolean; triggerHints: string }
const EMPTY_SC: ScenarioForm = { name: '', description: '', loadMode: 'auto', enabled: true, triggerHints: '' }

export function AiTrainPage() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = role === 'owner' || role === 'admin'
  const [learnHistoryOpen, setLearnHistoryOpen] = useState(false)
  // Đề xuất persona sinh từ học lịch sử đang chờ duyệt — hiện badge trên tile.
  const pendingQ = useLogicProposals(canEdit ? 'pending' : undefined)
  const pendingLearn = (pendingQ.data?.items ?? []).filter((p) =>
    p.source === 'learn_history' || p.source === 'learn_history_file').length

  const { data: config } = useAiConfig()
  const { data: budgetData } = useContextBudgets()
  // Backend TDVN không có /ai/channel-overrides — danh sách kênh lấy từ /zalo-accounts.
  const { data: channelAccounts } = useZaloAccounts()
  // Hook phải nằm TRƯỚC mọi `return` sớm bên dưới. Đặt sau chúng thì lượt render
  // đầu (đang tải) không gọi tới, lượt sau lại gọi — React đếm số hook lệch nhau
  // và ném lỗi #310, trắng cả màn.
  const locKenh = useBoLocKenh(channelAccounts ?? [])
  const { data: scenarioData } = useAiScenarios()
  const { data: kbCats } = useKnowledgeCategories()
  const { data: docFolders } = useDocFolders()
  const { data: tplData } = useBotTemplates()
  const startEval = useStartEvalRun()

  const [bot, setBot] = useState<AiBot | null>(null)
  const [loadErr, setLoadErr] = useState(false)
  const [form, setForm] = useState<AiBotInput>({})
  const [saving, setSaving] = useState(false)
  const [docVersions, setDocVersions] = useState<Record<string, number>>({})

  // ── Editor state (cột trái) ──
  const [sel, setSel] = useState<DocSel>({ kind: 'bot', field: 'personaPrompt', label: 'Tính cách & xưng hô của bot (riêng)' })
  const [docContent, setDocContent] = useState('')
  const [docMeta, setDocMeta] = useState<string>('')
  const [docLoading, setDocLoading] = useState(false)
  const [docSaving, setDocSaving] = useState(false)
  const [scForm, setScForm] = useState<ScenarioForm>(EMPTY_SC)

  // ── Dialog nguồn tri thức ──
  const [resourceOpen, setResourceOpen] = useState<null | 'products' | 'knowledge'>(null)
  const [addKnowledgeOpen, setAddKnowledgeOpen] = useState(false)
  // ── AI viết prompt: mở dialog + giữ bản trước để hoàn tác ──
  const [aiVietOpen, setAiVietOpen] = useState(false)
  const [banTruocAi, setBanTruocAi] = useState<string | null>(null)
  // ── Form tính cách / kiểm định ──
  const [personaFormOpen, setPersonaFormOpen] = useState(false)
  const [khungOpen, setKhungOpen] = useState(false)
  // Đếm khối đã tuỳ chỉnh / đã tắt để hiện ở mục KHUNG HƯỚNG DẪN.
  const khoiQ = useKhoiHuongDan(botId)
  const [evalRunId, setEvalRunId] = useState<string | null>(null)
  // Poll lịch sử kiểm định của riêng bot khi có lượt đang chạy (hook gọi vô
  // điều kiện — đặt trước mọi return sớm).
  const evalRunsQ = useEvalRuns(true, botId)

  useEffect(() => {
    if (!botId) return
    api.get(`/ai/bots/${botId}`)
      .then(({ data }) => {
        const b = data.bot as AiBot
        setBot(b)
        setForm({
          name: b.name, avatarEmoji: b.avatarEmoji, description: b.description,
          enabled: b.enabled, provider: b.provider, model: b.model,
          toolsJson: b.toolsJson, channelAccountIds: b.channelAccountIds,
          personaPrompt: b.personaPrompt, playbookPrompt: b.playbookPrompt, handoffPrompt: b.handoffPrompt,
          role: b.role, scenarioIds: b.scenarioIds, personaForm: b.personaForm,
        })
        setDocContent(b.personaPrompt ?? '')
        setDocMeta('Chỉ áp dụng cho bot này — bỏ trống để dùng tài liệu nền chung.')
      })
      .catch(() => setLoadErr(true))
    api.get('/ai/logic-docs')
      .then(({ data }) => {
        const map: Record<string, number> = {}
        for (const d of data.docs ?? []) map[d.type] = d.version
        setDocVersions(map)
      })
      .catch(() => { /* badge là phụ, lỗi bỏ qua */ })
  }, [botId])

  // Nạp nội dung tài liệu khi đổi lựa chọn
  useEffect(() => {
    let alive = true
    setBanTruocAi(null)
    if (sel.kind === 'bot') {
      setDocContent((form[sel.field] as string) ?? '')
      setDocMeta('Chỉ áp dụng cho bot này — bỏ trống để dùng tài liệu nền chung.')
      return
    }
    if (sel.kind === 'scenario-new') {
      setScForm(EMPTY_SC)
      setDocContent('')
      setDocMeta('Kịch bản mới — điền tên, mô tả (khi nào dùng) và nội dung xử lý.')
      return
    }
    setDocLoading(true)
    setDocContent('')
    setDocMeta('')
    const load = sel.kind === 'org'
      ? api.get(`/ai/logic-docs/${sel.type}`).then(({ data }) => ({
          content: data.doc?.content ?? '',
          meta: `Tài liệu nền dùng chung mọi bot · v${data.doc?.version ?? 1}`,
        }))
      : fetchScenario(sel.id).then((res) => {
          if (alive) {
            setScForm({
              name: res.scenario.name,
              description: res.scenario.description,
              loadMode: res.scenario.loadMode,
              enabled: res.scenario.enabled,
              triggerHints: res.scenario.triggerHints ?? '',
            })
          }
          return {
            content: res.scenario.content,
            meta: `Kịch bản dùng chung mọi bot · v${res.scenario.version}`,
          }
        })
    load
      .then((r) => { if (alive) { setDocContent(r.content); setDocMeta(r.meta) } })
      .catch((err) => { if (alive) { setDocMeta(''); toast.error(apiError(err)) } })
      .finally(() => { if (alive) setDocLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sel])

  if (loadErr) return <ErrorState message="Không tải được bot." />
  if (!bot || !config) return <Loading label="Đang tải màn train AI..." />

  const providers = config.availableProviders
  const channels = channelAccounts ?? []
  const scenarios = scenarioData?.scenarios ?? []
  const models = providers.find((p) => p.id === form.provider)?.models ?? []
  const set = (patch: AiBotInput) => setForm((f) => ({ ...f, ...patch }))

  type TraCuu = 'search_products' | 'search_knowledge'
  const toolsNen = (): BotToolsJson => form.toolsJson ?? {
    search_products: { enabled: true, guardrail: { categoryIds: [] } },
    search_knowledge: { enabled: true, guardrail: { categoryIds: [] } },
  }
  const toolEnabled = (name: TraCuu) => (form.toolsJson?.[name]?.enabled ?? true)
  const guardrailIds = (name: TraCuu) => form.toolsJson?.[name]?.guardrail?.categoryIds ?? []
  const patchTool = (name: TraCuu, patch: { enabled?: boolean; categoryIds?: string[] }) => {
    const base = toolsNen()
    const cur = base[name] ?? { enabled: true, guardrail: { categoryIds: [] } }
    const next: BotToolsJson = {
      ...base,
      [name]: {
        enabled: patch.enabled ?? cur.enabled,
        guardrail: { categoryIds: patch.categoryIds ?? cur.guardrail.categoryIds },
      },
    }
    set({ toolsJson: next })
    return next
  }
  // Công cụ hành động: tick = bot ĐƯỢC dùng; lưu dưới dạng danh sách bị gỡ.
  const congCuTat = form.toolsJson?.tatCongCu ?? []
  const datCongCu = (name: CongCuHanhDong, dung: boolean) => {
    const tat = dung ? congCuTat.filter((x) => x !== name) : [...new Set([...congCuTat, name])]
    const next: BotToolsJson = { ...toolsNen() }
    if (tat.length) next.tatCongCu = tat
    else delete next.tatCongCu
    set({ toolsJson: next })
  }

  // Phạm vi kịch bản: null = mọi kịch bản org (trừ seed mẫu tpl_*).
  const kichBanOrg = scenarios.filter((s) => !s.key.startsWith(TIEN_TO_KICH_BAN_MAU))
  const kichBanMau = scenarios.filter((s) => s.key.startsWith(TIEN_TO_KICH_BAN_MAU))
  const scIds = form.scenarioIds
  const kichBanDuocChon = (id: string, laMau: boolean) => (scIds === null || scIds === undefined ? !laMau : scIds.includes(id))
  const toggleKichBan = (id: string) => {
    // Từ "tất cả" chuyển sang danh sách chốt: bắt đầu từ mọi kịch bản org đang có.
    const nen = scIds ?? kichBanOrg.map((s) => s.id)
    set({ scenarioIds: nen.includes(id) ? nen.filter((x) => x !== id) : [...nen, id] })
  }

  const selectedChannels = form.channelAccountIds ?? []
  const toggleChannel = (id: string) => {
    set({ channelAccountIds: selectedChannels.includes(id) ? selectedChannels.filter((c) => c !== id) : [...selectedChannels, id] })
  }

  async function saveConfig() {
    setSaving(true)
    try {
      await updateAiBot(bot!.id, form)
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
      toast.success('Đã lưu cấu hình bot')
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setSaving(false)
    }
  }

  async function saveDoc() {
    setDocSaving(true)
    try {
      if (sel.kind === 'bot') {
        const patch = { [sel.field]: docContent } as AiBotInput
        set(patch)
        await updateAiBot(bot!.id, patch)
        toast.success('Đã lưu prompt của bot')
      } else if (sel.kind === 'org') {
        const docType = sel.type
        await api.put(`/ai/logic-docs/${docType}`, { content: docContent, changeNote: 'Sửa từ màn Train AI' })
        setDocVersions((m) => ({ ...m, [docType]: (m[docType] ?? 1) + 1 }))
        toast.success('Đã lưu tài liệu nền (áp dụng mọi bot)')
      } else if (sel.kind === 'scenario') {
        if (!scForm.name.trim() || !scForm.description.trim()) { toast.error('Kịch bản cần Tên và Mô tả'); return }
        await updateScenario(sel.id, {
          name: scForm.name, description: scForm.description, content: docContent,
          loadMode: scForm.loadMode, triggerHints: scForm.triggerHints || null, enabled: scForm.enabled,
        })
        await qc.invalidateQueries({ queryKey: aiKeys.scenarios })
        toast.success('Đã lưu kịch bản')
      } else {
        // Kịch bản mới — báo đích danh trường còn thiếu
        if (!scForm.name.trim()) { toast.error('Thiếu Tên kịch bản'); return }
        if (!scForm.description.trim()) { toast.error('Thiếu Mô tả — khi nào dùng'); return }
        if (!docContent.trim()) { toast.error('Thiếu Nội dung — hướng dẫn xử lý (khung soạn thảo lớn bên dưới)'); return }
        const res = await createScenario({
          name: scForm.name, description: scForm.description, content: docContent,
          loadMode: scForm.loadMode, triggerHints: scForm.triggerHints || null, enabled: scForm.enabled,
        })
        await qc.invalidateQueries({ queryKey: aiKeys.scenarios })
        // Bot đang chốt danh sách kịch bản → tự gắn kịch bản vừa tạo, kẻo
        // người dùng tạo xong mà bot không nạp.
        if (Array.isArray(form.scenarioIds)) {
          const next = [...form.scenarioIds, res.scenario.id]
          set({ scenarioIds: next })
          await updateAiBot(bot!.id, { scenarioIds: next })
        }
        setSel({ kind: 'scenario', id: res.scenario.id, label: `Kịch bản: ${res.scenario.name}` })
        toast.success('Đã tạo kịch bản mới')
      }
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setDocSaving(false)
    }
  }

  async function handleDeleteScenario() {
    if (sel.kind !== 'scenario') return
    if (!window.confirm(`Xoá kịch bản "${scForm.name}"? Áp dụng cho mọi bot.`)) return
    try {
      await deleteScenario(sel.id)
      await qc.invalidateQueries({ queryKey: aiKeys.scenarios })
      setSel({ kind: 'bot', field: 'personaPrompt', label: 'Tính cách & xưng hô của bot (riêng)' })
      toast.success('Đã xoá kịch bản')
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function apMau(role: VaiTroBot) {
    const giuPrompt = !!(form.personaPrompt || form.playbookPrompt)
      && !window.confirm(
        `Áp mẫu "${NHAN_VAI_TRO[role]}" và GHI ĐÈ tính cách / playbook / luật chuyển hiện có bằng nội dung mẫu?\n\n` +
        'Chọn "Huỷ" để chỉ đặt vai trò, kịch bản và công cụ theo mẫu, giữ nguyên prompt đã viết.',
      )
    try {
      const res = await applyBotTemplate(bot!.id, role, giuPrompt)
      const b = res.bot
      setBot(b)
      setForm((f) => ({
        ...f, role: b.role, scenarioIds: b.scenarioIds, toolsJson: b.toolsJson, description: b.description,
        personaPrompt: b.personaPrompt, playbookPrompt: b.playbookPrompt, handoffPrompt: b.handoffPrompt, personaForm: b.personaForm,
      }))
      if (sel.kind === 'bot') setDocContent((b[sel.field] as string) ?? '')
      await qc.invalidateQueries({ queryKey: aiKeys.bots })
      await qc.invalidateQueries({ queryKey: aiKeys.scenarios })
      toast.success(giuPrompt ? `Đã đặt vai trò ${NHAN_VAI_TRO[role]} (giữ prompt cũ)` : `Đã áp mẫu ${NHAN_VAI_TRO[role]}`)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  const evalRuns = evalRunsQ.data?.runs ?? []
  const dangKiemDinh = evalRuns.some((r) => r.status === 'running')
  const ketQuaEval = evalRuns.find((r) => r.status === 'done') ?? null

  async function chayKiemDinh() {
    try {
      const res = await startEval.mutateAsync({ botId: bot!.id })
      setEvalRunId(res.run.id)
      toast.success(`Đang chạy ${res.run.total} ca kiểm định — mỗi ca tốn phí AI thật`)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  // Loại tài liệu đang mở → loại prompt cho "AI viết".
  const loaiPrompt: LoaiPrompt = sel.kind === 'bot'
    ? (sel.field === 'personaPrompt' ? 'persona' : sel.field === 'playbookPrompt' ? 'playbook' : 'handoff_rules')
    : sel.kind === 'org' ? (sel.type as LoaiPrompt) : 'scenario'

  const selKey = sel.kind === 'bot' ? `bot:${sel.field}`
    : sel.kind === 'org' ? `org:${sel.type}`
    : sel.kind === 'scenario' ? `sc:${sel.id}` : 'sc:new'
  const isScenario = sel.kind === 'scenario' || sel.kind === 'scenario-new'

  // Bộ đếm ký tự theo ngân sách prompt của backend — cảnh báo trước khi bị CẮT NGẦM.
  // Persona/playbook (bot lẫn org) có hạn mức riêng; các doc nền khác map theo
  // đúng cách backend cắt; kịch bản dùng ngân sách CHUNG nên nêu tổng cho trung thực.
  const budgetCounter = (() => {
    const b = budgetData?.budgets
    if (!b) return null
    if (sel.kind === 'bot') {
      const cap = sel.field === 'personaPrompt' ? b.persona : sel.field === 'playbookPrompt' ? b.playbook : b.index
      return budgetCounterText(docContent.length, cap)
    }
    if (sel.kind === 'org') {
      return budgetCounterText(docContent.length, budgetForDocType(b, sel.type))
    }
    // Kịch bản: always + auto chia sẻ chung l0bScenarios mỗi lượt trả lời
    return budgetCounterText(docContent.length, b.l0bScenarios, { shared: true, sharedLabel: 'Tổng kịch bản vào prompt mỗi lượt' })
  })()

  const brainItem = (key: string, label: string, icon: string, docSel: DocSel, badge?: string) => (
    <button
      key={key}
      type="button"
      onClick={() => setSel(docSel)}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
        selKey === key ? 'bg-primary/10 font-semibold text-primary' : 'hover:bg-muted'
      }`}
    >
      <span>{icon}</span>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge && <Badge variant="outline" className="shrink-0 px-1.5 text-[10px]">{badge}</Badge>}
    </button>
  )

  const kbCatList = kbCats ?? []
  const kbGuardCount = guardrailIds('search_knowledge').length

  return (
    <div className="flex h-[calc(100vh-120px)] min-h-[560px] flex-col gap-3">
      {/* Header — tên bot + bật/tắt sửa trực tiếp tại đây */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/ai')}><ArrowLeft className="h-4 w-4" /></Button>
        <Input
          className="w-14 text-center text-xl" value={form.avatarEmoji ?? ''}
          onChange={(e) => set({ avatarEmoji: e.target.value.slice(0, 4) })} disabled={!canEdit}
          placeholder={DEFAULT_EMOJI}
        />
        <Input
          className="w-56 font-semibold" value={form.name ?? ''}
          onChange={(e) => set({ name: e.target.value })} disabled={!canEdit}
          placeholder="Tên bot"
        />
        <div className="flex items-center gap-1.5">
          <Switch checked={form.enabled ?? true} onCheckedChange={(v) => set({ enabled: v })} disabled={!canEdit} />
          <span className="text-xs text-muted-foreground">{form.enabled ?? true ? 'Đang bật' : 'Đang tắt'}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" disabled={!canEdit} title="Vai trò của bot — áp mẫu để nhận playbook, luật chuyển, kịch bản, công cụ của vai trò">
              <Wand2 className="h-3.5 w-3.5" />
              {form.role ? NHAN_VAI_TRO[form.role] : 'Chưa có vai trò'}
              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-72">
            <p className="px-2 py-1.5 text-xs text-muted-foreground">Áp mẫu vai trò lên bot này</p>
            {(tplData?.templates ?? []).map((t) => (
              <DropdownMenuItem key={t.role} onSelect={() => void apMau(t.role)}>
                <span className="mr-2">{t.emoji}</span>
                <span className="flex-1">{t.name}</span>
                {form.role === t.role && <Badge variant="secondary" className="text-[10px]">hiện tại</Badge>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline" onClick={() => void chayKiemDinh()} disabled={!canEdit || startEval.isPending || dangKiemDinh}
            title="Chạy bộ ca kiểm định gán cho bot này (và ca dùng chung) trong sandbox — không gửi gì cho khách"
          >
            {dangKiemDinh ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}
            {dangKiemDinh ? 'Đang kiểm định…' : 'Chạy kiểm định'}
            {ketQuaEval && !dangKiemDinh && (
              <Badge variant={ketQuaEval.passed / Math.max(ketQuaEval.total, 1) >= 0.8 ? 'success' : 'warning'} className="text-[10px]">
                {ketQuaEval.passed}/{ketQuaEval.total}
              </Badge>
            )}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={!canEdit}>
                <Radio className="h-4 w-4" /> Kênh áp dụng
                {selectedChannels.length > 0 && <Badge variant="secondary">{selectedChannels.length}</Badge>}
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72 p-2">
              <p className="px-1 pb-2 text-xs text-muted-foreground">
                Mỗi kênh chỉ thuộc 1 bot — gán ở đây sẽ gỡ khỏi bot khác. Nhớ bấm "Lưu cấu hình".
              </p>
              {channels.length === 0 ? (
                <p className="p-2 text-xs text-muted-foreground">Chưa có kênh nào được kết nối.</p>
              ) : (
                <>
                  <TheLocKenh loc={locKenh} />
                  <OTimKenh value={locKenh.tuKhoa} onChange={locKenh.datTuKhoa} placeholder="Tìm kênh…" />
                  <div className={CAO_TOI_DA_10_DONG}>
                    {locKenh.hienThi.map((ch) => (
                      <label key={ch.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted">
                        <input
                          type="checkbox" checked={selectedChannels.includes(ch.id)}
                          onChange={() => toggleChannel(ch.id)} disabled={!canEdit}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{ch.displayName || 'Kênh chưa đặt tên'}</span>
                          {ch.phone && (
                            <span className="block truncate text-[10.5px] tabular-nums text-muted-foreground">
                              {ch.phone}
                            </span>
                          )}
                        </span>
                        <Badge variant="outline" className="shrink-0">{platformName(ch.platform)}</Badge>
                      </label>
                    ))}
                  </div>
                  {locKenh.hienThi.length === 0 && (
                    <p className="p-2 text-center text-xs text-muted-foreground">
                      Không có kênh nào khớp{locKenh.tuKhoa ? ` "${locKenh.tuKhoa}"` : ''}
                    </p>
                  )}
                  {/* Kênh đã chọn nhưng bị lọc khuất vẫn phải đếm được, kẻo
                      tưởng bỏ chọn mất rồi lại tick lại. */}
                  {selectedChannels.length > 0 && (
                    <p className="border-t px-2 pt-1.5 text-[11px] text-muted-foreground">
                      Đang chọn {selectedChannels.length} kênh
                    </p>
                  )}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={saveConfig} disabled={!canEdit || saving}>
            <Save className="h-4 w-4" /> {saving ? 'Đang lưu...' : 'Lưu cấu hình'}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)_minmax(0,1.1fr)]">
        {/* ══ TRÁI: Editor tài liệu / kịch bản ══ */}
        <div className="flex min-h-0 flex-col gap-2 rounded-lg border bg-background p-3">
          <div className="flex items-center justify-between gap-2">
            <Label className="flex min-w-0 items-center gap-2">
              <BookOpenText className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{sel.label}</span>
            </Label>
            <div className="flex shrink-0 items-center gap-1">
              {banTruocAi !== null && (
                <Button
                  variant="ghost" size="sm" title="Hoàn tác — trả lại nội dung trước khi AI viết"
                  onClick={() => { setDocContent(banTruocAi); setBanTruocAi(null) }}
                >
                  <Undo2 className="h-3.5 w-3.5" /> Hoàn tác
                </Button>
              )}
              <Button
                variant="outline" size="sm" title="AI viết mới hoặc tối ưu tài liệu này (model GPT)"
                onClick={() => setAiVietOpen(true)} disabled={!canEdit || docLoading}
              >
                <Sparkles className="h-3.5 w-3.5 text-primary" /> AI viết
              </Button>
              {sel.kind === 'scenario' && (
                <Button variant="ghost" size="icon" title="Xoá kịch bản" onClick={handleDeleteScenario} disabled={!canEdit}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              )}
              <Button size="sm" onClick={saveDoc} disabled={!canEdit || docSaving || docLoading}>
                <Save className="h-3.5 w-3.5" /> {docSaving ? 'Đang lưu...' : sel.kind === 'scenario-new' ? 'Tạo kịch bản' : 'Lưu tài liệu'}
              </Button>
            </div>
          </div>
          {docMeta && <p className="text-xs text-muted-foreground">{docMeta}</p>}

          {/* Trường cấu hình động của kịch bản */}
          {isScenario && !docLoading && (
            <div className="space-y-2 rounded-md border bg-muted/20 p-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Tên kịch bản *</Label>
                  <Input value={scForm.name} onChange={(e) => setScForm((f) => ({ ...f, name: e.target.value }))} disabled={!canEdit} placeholder="vd: Tư vấn theo ngân sách" />
                </div>
                <div className="flex items-end gap-3">
                  <div className="flex-1">
                    <Label className="text-xs">Chế độ tải</Label>
                    <Select value={scForm.loadMode} onValueChange={(v) => setScForm((f) => ({ ...f, loadMode: v }))} disabled={!canEdit}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Theo ngữ cảnh</SelectItem>
                        <SelectItem value="always">Luôn tải</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1.5 pb-2">
                    <Switch checked={scForm.enabled} onCheckedChange={(v) => setScForm((f) => ({ ...f, enabled: v }))} disabled={!canEdit} />
                    <span className="text-xs">{scForm.enabled ? 'Bật' : 'Tắt'}</span>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs">Mô tả — khi nào dùng * <span className="font-normal text-muted-foreground">(để AI tự chọn kịch bản đúng chủ đề)</span></Label>
                <Input value={scForm.description} onChange={(e) => setScForm((f) => ({ ...f, description: e.target.value }))} disabled={!canEdit} placeholder="vd: Khách hỏi giá theo ngân sách, so sánh gói..." />
              </div>
              <div>
                <Label className="text-xs">Cách hỏi khác / từ đồng nghĩa <span className="font-normal text-muted-foreground">(giúp khớp ngữ cảnh chính xác hơn)</span></Label>
                <Input value={scForm.triggerHints} onChange={(e) => setScForm((f) => ({ ...f, triggerHints: e.target.value }))} disabled={!canEdit} placeholder="vd: tầm giá, khoảng bao nhiêu tiền, budget" />
              </div>
            </div>
          )}

          {docLoading ? (
            <Loading label="Đang tải tài liệu..." />
          ) : (
            <>
              {isScenario && (
                <Label className="text-xs">
                  Nội dung — hướng dẫn xử lý <span className="text-destructive">*</span>
                </Label>
              )}
              <textarea
                className="min-h-0 w-full flex-1 resize-none rounded-md border bg-transparent p-3 font-mono text-xs outline-none focus:ring-1 focus:ring-ring"
                value={docContent}
                onChange={(e) => setDocContent(e.target.value)}
                placeholder={isScenario ? 'Nội dung — hướng dẫn xử lý khi kịch bản khớp...' : 'Nội dung markdown...'}
                disabled={!canEdit}
              />
              {budgetCounter && (
                <p className={`shrink-0 text-[11px] ${budgetCounter.over ? 'text-destructive' : 'text-muted-foreground'}`}>
                  {budgetCounter.text}
                </p>
              )}
            </>
          )}
        </div>

        {/* ══ GIỮA: Cấu trúc bộ não + cấu hình chế độ ══ */}
        <div className="min-h-0 space-y-3 overflow-y-auto rounded-lg border bg-background p-3">
          <Input
            placeholder="Mô tả ngắn" value={form.description ?? ''}
            onChange={(e) => set({ description: e.target.value })} disabled={!canEdit}
          />

          <Separator />

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-[11px] font-bold tracking-wide text-muted-foreground">TÀI LIỆU BOT (RIÊNG)</p>
              <button
                type="button" disabled={!canEdit} title="Soạn tính cách bằng form — không cần viết prompt"
                className="rounded px-1.5 py-0.5 text-[11px] text-primary hover:bg-primary/10"
                onClick={() => setPersonaFormOpen(true)}
              >
                Soạn bằng form
              </button>
            </div>
            {brainItem('bot:personaPrompt', 'Tính cách & xưng hô của bot', '🎭', { kind: 'bot', field: 'personaPrompt', label: 'Tính cách & xưng hô của bot (riêng)' }, form.personaForm ? 'từ form' : undefined)}
            {brainItem('bot:playbookPrompt', 'Playbook của bot', '📋', { kind: 'bot', field: 'playbookPrompt', label: 'Playbook của bot (riêng)' })}
            {brainItem('bot:handoffPrompt', 'Khi nào chuyển nhân viên', '🙋', { kind: 'bot', field: 'handoffPrompt', label: 'Khi nào chuyển nhân viên (riêng)' })}
          </div>
          <div>
            <p className="mb-1 text-[11px] font-bold tracking-wide text-muted-foreground">KHUNG HƯỚNG DẪN (HỆ THỐNG)</p>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent/60"
              onClick={() => setKhungOpen(true)}
              title="Mọi câu lệnh hệ thống gửi cho mô hình và nguồn dữ liệu nạp vào lượt — bật/tắt, sửa, xem prompt cuối"
            >
              <span>🧩</span>
              <span className="min-w-0 flex-1 truncate">Khối hướng dẫn & nguồn nạp</span>
              {khoiQ.data && (() => {
                const ds = khoiQ.data.khoi
                const tuy = ds.filter((k) => k.ghiDe).length
                const tat = ds.filter((k) => !k.bat).length
                return (
                  <span className="shrink-0 text-[10.5px] text-muted-foreground">
                    {ds.length} mục{tuy ? ` · ${tuy} tuỳ chỉnh` : ''}{tat ? ` · ${tat} tắt` : ''}
                  </span>
                )
              })()}
            </button>
          </div>
          <div>
            <p className="mb-1 text-[11px] font-bold tracking-wide text-muted-foreground">TÀI LIỆU NỀN (DÙNG CHUNG)</p>
            {ORG_DOCS.map((d) =>
              brainItem(`org:${d.type}`, d.label, '📚', { kind: 'org', type: d.type, label: `${d.label} · nền` },
                docVersions[d.type] ? `v${docVersions[d.type]}` : undefined),
            )}
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-[11px] font-bold tracking-wide text-muted-foreground">KỊCH BẢN AI ({scenarios.length})</p>
              <button
                type="button" title="Thêm kịch bản mới" disabled={!canEdit}
                className="rounded p-0.5 text-primary hover:bg-primary/10"
                onClick={() => setSel({ kind: 'scenario-new', label: 'Kịch bản mới' })}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-1 px-2 text-[11px] text-muted-foreground">
              {scIds == null
                ? 'Bot nạp mọi kịch bản của tổ chức. Bỏ tick một kịch bản để chốt danh sách riêng.'
                : `Bot chỉ nạp ${scIds.length} kịch bản đã tick.`}
              {scIds != null && (
                <button type="button" className="ml-1 text-primary hover:underline" disabled={!canEdit} onClick={() => set({ scenarioIds: null })}>Dùng tất cả</button>
              )}
            </p>
            {[...kichBanOrg, ...kichBanMau].map((s) => {
              const laMau = s.key.startsWith(TIEN_TO_KICH_BAN_MAU)
              return (
                <div key={`sc:${s.id}`} className="flex items-center gap-1">
                  <input
                    type="checkbox" className="ml-1 shrink-0" disabled={!canEdit}
                    checked={kichBanDuocChon(s.id, laMau)} onChange={() => toggleKichBan(s.id)}
                    title={kichBanDuocChon(s.id, laMau) ? 'Bot đang nạp kịch bản này' : 'Bot không nạp kịch bản này'}
                  />
                  <div className="min-w-0 flex-1">
                    {brainItem(`sc:${s.id}`, s.name, s.enabled ? '🟢' : '⚪', { kind: 'scenario', id: s.id, label: `Kịch bản: ${s.name}` }, laMau ? 'mẫu' : `v${s.version}`)}
                  </div>
                </div>
              )
            })}
            {sel.kind === 'scenario-new' && (
              <div className="rounded-md bg-primary/10 px-2 py-1.5 text-sm font-semibold text-primary">＋ Kịch bản mới (chưa lưu)</div>
            )}
            {scenarios.length === 0 && sel.kind !== 'scenario-new' && (
              <p className="px-2 text-xs text-muted-foreground">Chưa có kịch bản nào — bấm + để thêm.</p>
            )}
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <p className="text-[11px] font-bold tracking-wide text-muted-foreground">NGUỒN TRI THỨC</p>
              <button
                type="button" title="Thêm tài nguyên tri thức mới" disabled={!canEdit}
                className="rounded p-0.5 text-primary hover:bg-primary/10"
                onClick={() => setAddKnowledgeOpen(true)}
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
            {/* Sản phẩm: một nguồn duy nhất là Tài liệu bán hàng dựng từ API FMRP
                (58 mã đang bán, mô tả + ảnh + giá thật lúc trả lời). Không còn
                chọn nhóm theo bảng nội bộ — bảng đó đã ngừng dùng. */}
            {/* div role=button (không phải <button>) vì bên trong có Switch cũng là <button> — lồng nhau là HTML sai. */}
            <div
              role="button" tabIndex={0}
              onClick={() => setResourceOpen('products')}
              onKeyDown={(e) => { if (e.key === 'Enter') setResourceOpen('products') }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              title="Bot đọc tài liệu bán hàng (Tài liệu bán hàng → cây thư mục). Bấm để giới hạn theo thư mục."
            >
              <span>🛒</span>
              <span className="min-w-0 flex-1 truncate">Sản phẩm (FMRP · tài liệu bán hàng)</span>
              <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Switch checked={toolEnabled('search_products')} onCheckedChange={(v) => patchTool('search_products', { enabled: v })} disabled={!canEdit} />
                <Badge variant="outline" className="px-1.5 text-[10px]">
                  {guardrailIds('search_products').length === 0 ? 'toàn bộ' : `${guardrailIds('search_products').length} thư mục`}
                </Badge>
              </span>
            </div>
            <div
              role="button" tabIndex={0}
              onClick={() => setResourceOpen('knowledge')}
              onKeyDown={(e) => { if (e.key === 'Enter') setResourceOpen('knowledge') }}
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
            >
              <span>📚</span>
              <span className="min-w-0 flex-1 truncate">Kiến thức / FAQ (từ DB)</span>
              <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Switch checked={toolEnabled('search_knowledge')} onCheckedChange={(v) => patchTool('search_knowledge', { enabled: v })} disabled={!canEdit} />
                <Badge variant="outline" className="px-1.5 text-[10px]">
                  {kbGuardCount === 0 ? 'tất cả' : `${kbGuardCount} nhóm`}
                </Badge>
              </span>
            </div>
            {/* Học giọng điệu nhân viên thật từ hội thoại cũ — BE learn-history đã có,
                kết quả là ĐỀ XUẤT sửa persona nằm chờ duyệt (không tự áp). */}
            {canEdit && (
              <button
                type="button"
                onClick={() => setLearnHistoryOpen(true)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted"
              >
                <span>💬</span>
                <span className="min-w-0 flex-1 truncate">Lịch sử tin nhắn</span>
                {pendingLearn > 0 ? (
                  <Badge variant="warning" className="px-1.5 text-[10px]">{pendingLearn} đề xuất chờ duyệt</Badge>
                ) : (
                  <Badge variant="outline" className="px-1.5 text-[10px]">học giọng điệu</Badge>
                )}
              </button>
            )}
            <p className="px-2 pt-1 text-[11px] text-muted-foreground">
              Bấm vào từng nguồn để giới hạn thư mục / nhóm bot được tra cứu (chặn trong code).
            </p>
          </div>

          <div>
            <p className="mb-1 text-[11px] font-bold tracking-wide text-muted-foreground">CÔNG CỤ HÀNH ĐỘNG</p>
            {CONG_CU_HANH_DONG.map((c) => {
              const dung = !congCuTat.includes(c.name)
              return (
                <label key={c.name} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm hover:bg-muted" title={c.moTa}>
                  <input type="checkbox" checked={dung} onChange={(e) => datCongCu(c.name, e.target.checked)} disabled={!canEdit} />
                  <span className={`min-w-0 flex-1 truncate ${dung ? '' : 'text-muted-foreground line-through'}`}>{c.label}</span>
                </label>
              )
            })}
            <p className="px-2 pt-1 text-[11px] text-muted-foreground">
              Bỏ tick = bot không có công cụ đó (không khai với mô hình). Bot CSKH nên bỏ "Lên đơn nháp".
            </p>
          </div>

          <Separator />

          <Label>Mô hình</Label>
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={form.provider ?? '__default__'}
              onValueChange={(v) => {
                if (v === '__default__') set({ provider: null, model: null })
                else set({ provider: v, model: providers.find((p) => p.id === v)?.models[0]?.value ?? null })
              }}
              disabled={!canEdit}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Theo cấu hình chung</SelectItem>
                {providers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={form.model ?? ''} onValueChange={(v) => set({ model: v })} disabled={!canEdit || !form.provider}>
              <SelectTrigger><SelectValue placeholder="Mặc định" /></SelectTrigger>
              <SelectContent>
                {models.map((m) => <SelectItem key={m.value} value={m.value}>{m.title}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ══ PHẢI: Chat thử ══ */}
        <div className="flex min-h-0 flex-col rounded-lg border bg-background p-3">
          <DemoChat botId={bot.id} botName={form.name || 'Bot'} botEmoji={form.avatarEmoji || DEFAULT_EMOJI} />
        </div>
      </div>

      {/* ── Dialog: chọn danh mục nguồn tri thức từ DB ── */}
      {resourceOpen && (
        <ResourcePickerDialog
          title={resourceOpen === 'products' ? 'Thư mục tài liệu bán hàng bot được đọc' : 'Nhóm kiến thức bot được đọc'}
          items={resourceOpen === 'products'
            ? (docFolders ?? []).map((f) => ({ id: f.id, name: f.name, parentId: f.parentId, count: f.assetCount }))
            : kbCatList.map((c) => ({ id: c.id, name: c.name, parentId: c.parentId, count: c.entryCount }))}
          selected={guardrailIds(resourceOpen === 'products' ? 'search_products' : 'search_knowledge')}
          canEdit={canEdit}
          onClose={() => setResourceOpen(null)}
          onSave={async (ids) => {
            const tool = resourceOpen === 'products' ? 'search_products' : 'search_knowledge'
            const nextTools = patchTool(tool, { categoryIds: ids })
            try {
              await updateAiBot(bot.id, { toolsJson: nextTools })
              toast.success('Đã lưu nguồn tri thức của bot')
            } catch (err) {
              toast.error(apiError(err))
            }
            setResourceOpen(null)
          }}
        />
      )}

      {/* ── Dialog: học từ lịch sử tin nhắn (nguồn tri thức 💬) ── */}
      <LearnHistoryDialog open={learnHistoryOpen} onOpenChange={setLearnHistoryOpen} />

      {/* ── Dialog: thêm mới knowledge (Text / Product / Sheets / CSV) ── */}
      {addKnowledgeOpen && (
        <CreateKnowledgeDialog onClose={() => setAddKnowledgeOpen(false)} />
      )}

      {/* ── Dialog: AI viết / tối ưu tài liệu đang mở ── */}
      {aiVietOpen && (
        <AiVietPromptDialog
          loai={loaiPrompt}
          noiDungHienTai={docContent}
          botId={sel.kind === 'bot' ? bot.id : undefined}
          kichBan={isScenario ? { name: scForm.name, description: scForm.description, triggerHints: scForm.triggerHints } : undefined}
          onClose={() => setAiVietOpen(false)}
          onKetQua={(kq) => {
            setBanTruocAi(docContent)
            setDocContent(kq.content)
            if (kq.kichBan && isScenario) {
              setScForm((f) => ({
                ...f,
                name: f.name.trim() || kq.kichBan!.name,
                description: f.description.trim() || kq.kichBan!.description,
                triggerHints: f.triggerHints.trim() || kq.kichBan!.triggerHints,
              }))
            }
            toast.success('AI đã viết xong — xem lại rồi bấm Lưu tài liệu')
          }}
        />
      )}

      {/* ── Dialog: khung hướng dẫn (khối prompt + nguồn nạp) ── */}
      {bot && khungOpen && (
        <KhungHuongDanDialog
          botId={bot.id} botName={form.name || bot.name || 'Bot'} canEdit={canEdit}
          open={khungOpen} onClose={() => setKhungOpen(false)}
        />
      )}

      {/* ── Dialog: soạn tính cách bằng form → BE biên thành persona ── */}
      {personaFormOpen && (
        <PersonaFormDialog
          initial={form.personaForm ?? PERSONA_FORM_TRONG}
          role={form.role ?? 'sales'}
          canEdit={canEdit}
          onClose={() => setPersonaFormOpen(false)}
          onSave={async (pf) => {
            try {
              const role = form.role ?? 'sales'
              const r = await previewPersona(pf, role)
              const patch: AiBotInput = { personaForm: pf, personaPrompt: r.personaPrompt, ...(form.role ? {} : { role }) }
              set(patch)
              await updateAiBot(bot.id, patch)
              if (sel.kind === 'bot' && sel.field === 'personaPrompt') setDocContent(r.personaPrompt)
              toast.success('Đã lưu tính cách từ form')
              setPersonaFormOpen(false)
            } catch (err) {
              toast.error(apiError(err))
            }
          }}
        />
      )}

      {/* ── Dialog: kết quả kiểm định ── */}
      <RunDetailDialog runId={evalRunId} onClose={() => setEvalRunId(null)} />
    </div>
  )
}

function PersonaFormDialog({ initial, role, canEdit, onClose, onSave }: {
  initial: PersonaForm
  role: VaiTroBot
  canEdit: boolean
  onClose: () => void
  onSave: (pf: PersonaForm) => Promise<void>
}) {
  const [pf, setPf] = useState<PersonaForm>(initial)
  const [saving, setSaving] = useState(false)
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Tính cách bot — soạn bằng form</DialogTitle>
          <DialogDescription>
            Điền form, hệ thống tự viết prompt tính cách cho vai trò {NHAN_VAI_TRO[role]}. Lưu sẽ thay ô "Tính cách & xưng hô" hiện tại.
          </DialogDescription>
        </DialogHeader>
        <PersonaFormFields value={pf} onChange={setPf} role={role} disabled={!canEdit} />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button
            disabled={!canEdit || saving}
            onClick={async () => { setSaving(true); try { await onSave(chotPersonaForm(pf)) } finally { setSaving(false) } }}
          >
            <Save className="h-4 w-4" /> {saving ? 'Đang lưu…' : 'Lưu & sinh prompt'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Chat thử (sandbox thật, chạy đúng pipeline) ─────────────────────
type ChatMsg = { role: 'user' | 'bot' | 'system'; text: string }

function DemoChat({ botId, botName, botEmoji }: { botId: string; botName: string; botEmoji: string }) {
  const [msgs, setMsgs] = useState<ChatMsg[]>([])
  const [input, setInput] = useState('')
  // Thử "khách gửi ảnh": dán URL ảnh (/uploads/... hoặc https). Gửi kèm tin kế tiếp.
  const [anhUrl, setAnhUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const convRef = useRef<string | null>(null)
  const endRef = useRef<HTMLDivElement>(null)

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [msgs])

  const canSend = useMemo(() => (!!input.trim() || !!anhUrl.trim()) && !busy, [input, anhUrl, busy])

  async function send() {
    const text = input.trim()
    const anh = anhUrl.trim()
    if ((!text && !anh) || busy) return
    setInput('')
    setAnhUrl('')
    setMsgs((m) => [...m, { role: 'user', text: anh ? `${text ? text + '\n' : ''}🖼 ${anh}` : text }])
    setBusy(true)
    try {
      if (!convRef.current) {
        const conv = await createSandboxConversation(`Demo — ${botName}`)
        convRef.current = conv.conversationId
      }
      const res = await simulateBotReply(convRef.current, text, botId, anh ? [anh] : [])
      if (res.handoff?.should) {
        setMsgs((m) => [...m, { role: 'system', text: `↪ Bot chuyển nhân viên: ${res.handoff?.reason || ''}` }])
      } else if (res.reply) {
        setMsgs((m) => [...m, { role: 'bot', text: res.reply! }])
      } else if (res.error) {
        // Phân biệt rõ hai chuyện khác hẳn nhau: AI chủ động im, và AI gọi model
        // thất bại. Gộp chung thì người test đi sửa prompt trong khi lỗi thật là
        // hết hạn mức token mỗi phút của OpenAI.
        setMsgs((m) => [...m, { role: 'system', text: `⚠️ Không gọi được model: ${res.error}` }])
      } else {
        setMsgs((m) => [...m, { role: 'system', text: '(Bot quyết định không trả lời lượt này)' }])
      }
    } catch (err) {
      setMsgs((m) => [...m, { role: 'system', text: `Lỗi: ${apiError(err)}` }])
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Label className="mb-2 flex items-center gap-2">
        <MessageSquareText className="h-4 w-4 text-primary" /> Demo &amp; Review
      </Label>
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg border bg-muted/30 p-3">
        {msgs.length === 0 && (
          <p className="py-10 text-center text-xs text-muted-foreground">
            Sandbox thật — chạy đúng bộ não đang soạn bên trái.<br />Mỗi tin nhắn tốn phí AI thật.
          </p>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {m.role === 'system' ? (
              <span className="mx-auto text-center text-xs italic text-muted-foreground">{m.text}</span>
            ) : (
              <div className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${
                m.role === 'user' ? 'bg-primary text-primary-foreground' : 'border bg-background'
              }`}>
                {m.role === 'bot' && <span className="mr-1">{botEmoji}</span>}{m.text}
              </div>
            )}
          </div>
        ))}
        {busy && <p className="text-xs italic text-muted-foreground">{botEmoji} đang soạn trả lời...</p>}
        <div ref={endRef} />
      </div>
      <div className="mt-2 space-y-1.5">
        <Input
          className="h-8 text-xs"
          placeholder="URL ảnh khách gửi (tuỳ chọn) — /uploads/... hoặc https://"
          value={anhUrl}
          onChange={(e) => setAnhUrl(e.target.value)}
          disabled={busy}
          title="Thử AI đọc ảnh: dán link ảnh, gửi kèm tin kế tiếp"
        />
        <div className="flex gap-2">
          <Input
            placeholder="Nhập tin thử như khách hàng..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send() } }}
            disabled={busy}
          />
          <Button onClick={() => void send()} disabled={!canSend}><SendHorizonal className="h-4 w-4" /></Button>
        </div>
      </div>
    </>
  )
}
